/**
 * The single place the browser talks to the API.
 *
 * The active business travels in a header rather than in every URL, so any
 * screen can be pointed at a different business by changing one value.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * Whatever the server attached. Shape varies by code — a 422 sends field
   * errors, a 402 sends the allowance that was hit — so it is read through
   * the guarded accessors in errors.ts rather than being typed as one thing.
   */
  readonly details?: unknown;

  constructor(
    status: number,
    message: string,
    code: string,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let activeBusinessId: string | null = null;

export function setActiveBusinessId(id: string | null): void {
  activeBusinessId = id;
}

export function getActiveBusinessId(): string | null {
  return activeBusinessId;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the business header — used by auth and public endpoints. */
  unscoped?: boolean;
  signal?: AbortSignal;
}

/**
 * Where the API lives.
 *
 * Empty in development and in a single-origin deployment, where Vite's proxy
 * or the same host serves /api. Set to https://api.atronova.com for the split
 * deployment, where the client is static on one subdomain and the API runs on
 * another. Both are subdomains of atronova.com, so the session cookie is still
 * same-site and behaves normally — but the requests are cross-*origin*, which
 * is why every fetch below uses `credentials: 'include'` rather than
 * 'same-origin'. With 'same-origin' the browser silently drops the cookie and
 * every call comes back 401.
 */
export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/** Absolute URL for a server-relative asset path such as /uploads/x/logo.png. */
export function assetUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE}${path}`;
}

/** The credentials mode every request must use. See API_BASE. */
export const CREDENTIALS: RequestCredentials = API_BASE ? 'include' : 'same-origin';

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path, API_BASE || window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (!options.unscoped && activeBusinessId) headers['x-business-id'] = activeBusinessId;

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    credentials: CREDENTIALS,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // A non-JSON body from a proxy or crash still needs to surface usefully.
      if (!response.ok) throw new ApiError(response.status, text.slice(0, 200), 'invalid_response');
      return text as T;
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: { message?: string; code?: string; details?: unknown } })
      ?.error;
    throw new ApiError(
      response.status,
      error?.message ?? `Request failed (${response.status})`,
      error?.code ?? 'unknown',
      error?.details as { field: string; message: string }[] | undefined,
    );
  }

  return payload as T;
}

export const api = {
  get: <T,>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  post: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Auth and public endpoints, which must not carry a business header. */
  unscoped: {
    get: <T,>(path: string) => request<T>(path, { unscoped: true }),
    post: <T,>(path: string, body?: unknown) =>
      request<T>(path, { method: 'POST', body, unscoped: true }),
    patch: <T,>(path: string, body?: unknown) =>
      request<T>(path, { method: 'PATCH', body, unscoped: true }),
    delete: <T,>(path: string) => request<T>(path, { method: 'DELETE', unscoped: true }),
  },
};
